import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { ArrowLeft, Edit, FileText, Receipt, ClipboardList, Tag, Loader2, FileCheck, Ship, Plane, Download, Archive, CheckCircle2, AlertCircle, Send, Mail, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const statusColors = {
  documentacion_inicial: "bg-amber-100 text-amber-800",
  en_planta: "bg-orange-100 text-orange-800",
  en_reconocimiento: "bg-yellow-100 text-yellow-800",
  listo_para_cargar: "bg-lime-100 text-lime-800",
  en_transito: "bg-sky-100 text-sky-800",
  en_destino: "bg-cyan-100 text-cyan-800",
  entregado_cliente: "bg-teal-100 text-teal-800",
  completado: "bg-emerald-100 text-emerald-800",
  anulado: "bg-gray-100 text-gray-800",
  rechazado: "bg-red-100 text-red-800",
  otros: "bg-purple-100 text-purple-800",
  pendiente: "bg-amber-100 text-amber-800",
  recibido: "bg-cyan-100 text-cyan-800",
};

const statusLabels = {
  documentacion_inicial: "Documentación inicial",
  en_planta: "En la planta",
  en_reconocimiento: "En reconocimiento",
  listo_para_cargar: "Listo para cargar",
  en_transito: "En tránsito",
  en_destino: "En destino",
  entregado_cliente: "Entregado a cliente",
  completado: "Completado",
  anulado: "Anulado",
  rechazado: "Rechazado",
  otros: "Otros",
  pendiente: "Documentación inicial",
  recibido: "En destino",
};

// Labels para tipos de documento
const docTypeLabels = {
  factura_origen: "Factura Origen",
  factura_cliente: "Factura Cliente",
  packing_list: "Packing List",
  etiqueta: "Etiquetas",
  bl: "BL",
  awb: "AWB",
  cso: "CSO",
  certificado_captura: "Cert. Captura",
  certificado_sanitario: "Cert. Sanitario",
  certificado_exportacion: "Cert. Exportación",
  dua: "DUA",
  otros: "Otros",
};

// Documentos visibles para clientes
const clientVisibleDocs = [
  'factura_cliente', 'packing_list', 'etiqueta', 'bl', 'awb',
  'certificado_sanitario', 'certificado_captura', 'cso'
];

// Documentos obligatorios para exportación/importación
// NOTA: Anexo IV se maneja dinámicamente según si el producto lo requiere
const DOCUMENTOS_OBLIGATORIOS = [
  { key: "certificado_exportacion", label: "Cert. Exportación" },
  { key: "packing_list", label: "Packing List" },
  { key: "bl", label: "BL" },
  { key: "etiqueta", label: "Etiqueta" },
  { key: "certificado_captura", label: "Cert. Captura" },
  { key: "cso", label: "CSO (Sanitario)" },
  { key: "factura_origen", label: "Factura Origen" },
];

export default function BatchView() {
  const { id: batchId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isProveedor = user?.role === "proveedor";
  const isCliente = user?.role === "cliente";
  const isAdmin = user?.role === "admin" || user?.role === "admin_consultor";
  const [loading, setLoading] = useState(true);
  const [batch, setBatch] = useState(null);
  const [product, setProduct] = useState(null);
  const [batchDocuments, setBatchDocuments] = useState([]);
  const [supplier, setSupplier] = useState(null);
  const [client, setClient] = useState(null);
  const [generatingBackup, setGeneratingBackup] = useState(false);
  const [regeneratingPdfs, setRegeneratingPdfs] = useState(false);
  
  // Estados para envío de documentación
  const [showSendDocModal, setShowSendDocModal] = useState(false);
  const [sendingDocs, setSendingDocs] = useState(false);
  const [docInfo, setDocInfo] = useState(null);
  const [emailOperador, setEmailOperador] = useState("");
  const [emailCliente, setEmailCliente] = useState("");
  const [emailProveedor, setEmailProveedor] = useState("");
  const [enviarAOperador, setEnviarAOperador] = useState(true);
  const [enviarACliente, setEnviarACliente] = useState(true);
  const [enviarAProveedor, setEnviarAProveedor] = useState(false);
  // Nombres de destinatarios (para personalizar el saludo del email)
  const [nombreOperador, setNombreOperador] = useState("");
  const [nombreCliente, setNombreCliente] = useState("");
  const [nombreProveedor, setNombreProveedor] = useState("");
  // Documentos seleccionados para enviar (por tipo de destinatario)
  const [docsSeleccionadosOperador, setDocsSeleccionadosOperador] = useState([]);
  const [docsSeleccionadosCliente, setDocsSeleccionadosCliente] = useState([]);
  const [docsSeleccionadosProveedor, setDocsSeleccionadosProveedor] = useState([]);
  // Comentarios opcionales para el email
  const [comentariosEmail, setComentariosEmail] = useState("");

  // Lista de todos los tipos de documentos disponibles para enviar
  const TODOS_DOCS_ENVIO = [
    { key: "certificado_exportacion", label: "Cert. Exportación" },
    { key: "packing_list", label: "Packing List" },
    { key: "bl", label: "BL" },
    { key: "etiqueta", label: "Etiquetas" },
    { key: "certificado_captura", label: "Cert. Captura" },
    { key: "cso", label: "CSO" },
    { key: "anexo_iv", label: "Anexo IV" },
    { key: "factura_origen", label: "Factura Origen" },
  ];

  // Calcular documentos obligatorios incluyendo Anexo IV si el producto lo requiere
  const documentosObligatorios = React.useMemo(() => {
    const docs = [...DOCUMENTOS_OBLIGATORIOS];
    // Si el producto requiere Anexo IV, añadirlo a la lista
    if (product?.requiere_anexo_iv !== false) {
      docs.push({ key: "anexo_iv", label: "Anexo IV" });
    }
    return docs;
  }, [product]);

  useEffect(() => {
    fetchData();
  }, [batchId]);

  // Función para generar backup ZIP del lote
  const handleBackupLote = async () => {
    setGeneratingBackup(true);
    try {
      const response = await api.get(`/batches/${batchId}/backup`, {
        responseType: 'blob'
      });
      
      // Crear URL del blob y descargar
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Usar nombre por defecto del lote
      const filename = `BACKUP_${batch?.numero_lote || batchId}_${new Date().toISOString().slice(0,10)}.zip`;
      link.download = filename;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("Copia de seguridad generada correctamente");
    } catch (error) {
      console.error("Error generando backup:", error);
      if (error.response?.status === 404) {
        toast.error("No hay documentos para este lote");
      } else if (error.response?.status === 403) {
        toast.error("No tiene permisos para descargar este lote");
      } else {
        toast.error("Error al generar la copia de seguridad");
      }
    } finally {
      setGeneratingBackup(false);
    }
  };

  // Función para regenerar todos los PDFs del lote
  const handleRegeneratePdfs = async () => {
    const confirm = window.confirm(
      "¿Desea regenerar todos los PDFs de este lote?\n\n" +
      "Esto abrirá las páginas de Factura Origen, Factura Cliente y Packing List para que pueda guardar cada PDF.\n\n" +
      "Los PDFs se guardarán automáticamente en el servidor."
    );
    
    if (!confirm) return;
    
    setRegeneratingPdfs(true);
    try {
      // Obtener información de qué PDFs necesitan regenerarse
      const response = await api.post(`/batches/${batchId}/regenerate-all-pdfs`);
      const data = response.data;
      
      toast.info(`Documentos sin archivo: ${data.resumen.sin_archivo}. Abriendo páginas para regenerar...`);
      
      // Abrir las páginas de factura y packing list en nuevas pestañas
      // El usuario deberá hacer clic en "Guardar PDF" en cada una
      
      // Factura Origen
      if (data.facturas.some(f => f.tipo === "origen")) {
        window.open(`/batches/${batchId}/invoice-origin`, '_blank');
      }
      
      // Factura Cliente  
      if (data.facturas.some(f => f.tipo === "destino" || f.tipo === "cliente")) {
        window.open(`/batches/${batchId}/invoice-client`, '_blank');
      }
      
      // Packing List
      window.open(`/batches/${batchId}/packing-list`, '_blank');
      
      toast.success("Se abrieron las páginas. Haga clic en 'Guardar PDF' en cada una.");
      
    } catch (error) {
      console.error("Error al regenerar PDFs:", error);
      toast.error("Error al obtener información del lote");
    } finally {
      setRegeneratingPdfs(false);
    }
  };

  // Función para abrir modal de envío de documentación
  const handleOpenSendDocModal = async () => {
    try {
      const response = await api.get(`/batches/${batchId}/documentation-info`);
      const info = response.data;
      setDocInfo(info);
      setEmailOperador(info.email_operador || "");
      setEmailCliente(info.email_cliente || "");
      setEmailProveedor(info.email_proveedor || "");
      setEnviarAOperador(!!info.email_operador);
      setEnviarACliente(!!info.email_cliente);
      setEnviarAProveedor(false); // Por defecto desactivado
      // Inicializar documentos seleccionados con todos los tipos disponibles
      const allDocKeys = TODOS_DOCS_ENVIO.map(d => d.key);
      setDocsSeleccionadosOperador(allDocKeys);
      // Para cliente, todos excepto factura_origen
      setDocsSeleccionadosCliente(allDocKeys.filter(k => k !== "factura_origen"));
      setDocsSeleccionadosProveedor(allDocKeys);
      setComentariosEmail("");
      setNombreOperador("");
      setNombreCliente("");
      setNombreProveedor("");
      setShowSendDocModal(true);
    } catch (error) {
      toast.error("Error al obtener información de documentación");
    }
  };

  // Función para enviar documentación
  const handleSendDocumentation = async () => {
    if (!enviarAOperador && !enviarACliente && !enviarAProveedor) {
      toast.error("Selecciona al menos un destinatario");
      return;
    }
    if (enviarAOperador && !emailOperador) {
      toast.error("Introduce el email del operador logístico");
      return;
    }
    if (enviarACliente && !emailCliente) {
      toast.error("Introduce el email del cliente");
      return;
    }
    if (enviarAProveedor && !emailProveedor) {
      toast.error("Introduce el email del proveedor");
      return;
    }
    // Verificar que hay al menos un documento seleccionado
    if (enviarAOperador && docsSeleccionadosOperador.length === 0) {
      toast.error("Selecciona al menos un documento para el operador");
      return;
    }
    if (enviarACliente && docsSeleccionadosCliente.length === 0) {
      toast.error("Selecciona al menos un documento para el cliente");
      return;
    }
    if (enviarAProveedor && docsSeleccionadosProveedor.length === 0) {
      toast.error("Selecciona al menos un documento para el proveedor");
      return;
    }

    setSendingDocs(true);
    try {
      const response = await api.post(`/batches/${batchId}/send-documentation`, {
        email_operador: emailOperador,
        email_cliente: emailCliente,
        email_proveedor: emailProveedor,
        enviar_a_operador: enviarAOperador,
        enviar_a_cliente: enviarACliente,
        enviar_a_proveedor: enviarAProveedor,
        // Nombres de destinatarios personalizados
        nombre_operador: nombreOperador || docInfo?.nombre_operador || "",
        nombre_cliente: nombreCliente || docInfo?.nombre_cliente || "",
        nombre_proveedor: nombreProveedor || docInfo?.nombre_proveedor || "",
        // Documentos seleccionados para cada destinatario
        docs_operador: docsSeleccionadosOperador,
        docs_cliente: docsSeleccionadosCliente,
        docs_proveedor: docsSeleccionadosProveedor,
        // Comentarios opcionales
        comentarios: comentariosEmail
      });

      const { resultados } = response.data;
      let mensaje = "Documentación enviada: ";
      const enviados = [];
      
      if (resultados.operador === "enviado") enviados.push("Operador Logístico");
      if (resultados.cliente === "enviado") enviados.push("Cliente");
      if (resultados.proveedor === "enviado") enviados.push("Proveedor");
      
      if (enviados.length > 0) {
        toast.success(mensaje + enviados.join(", "));
      }
      
      if (resultados.operador === "error") {
        toast.error("Error al enviar al operador logístico");
      }
      if (resultados.cliente === "error") {
        toast.error("Error al enviar al cliente");
      }
      if (resultados.proveedor === "error") {
        toast.error("Error al enviar al proveedor");
      }
      if (resultados.operador === "sin_archivos" || resultados.cliente === "sin_archivos" || resultados.proveedor === "sin_archivos") {
        toast.warning("Algunos documentos no tienen archivo físico adjunto");
      }

      setShowSendDocModal(false);
    } catch (error) {
      console.error("Error enviando documentación:", error);
      toast.error(error.response?.data?.detail || "Error al enviar la documentación");
    } finally {
      setSendingDocs(false);
    }
  };

  // Diagnosticar documentos del lote
  const handleDiagnosticar = async () => {
    try {
      const response = await api.get(`/documents/diagnostico/${batch.numero_lote}`);
      const data = response.data;
      
      let mensaje = `=== DIAGNÓSTICO DEL LOTE ${batch.numero_lote} ===\n\n`;
      mensaje += `Total documentos: ${data.total_documentos}\n`;
      mensaje += `Con archivo: ${data.resumen.con_archivo}\n`;
      mensaje += `Sin archivo: ${data.resumen.sin_archivo}\n\n`;
      
      mensaje += `--- DETALLE DE DOCUMENTOS ---\n`;
      data.documentos.forEach(d => {
        const estado = d.existe_archivo ? "✅" : "❌";
        mensaje += `${estado} ${d.tipo_documento}: ${d.nombre_archivo}\n`;
        if (d.problema) {
          mensaje += `   PROBLEMA: ${d.problema}\n`;
        }
        if (d.ruta_archivo) {
          mensaje += `   Ruta: ${d.ruta_archivo}\n`;
        }
      });
      
      // Mostrar en consola de forma legible
      console.log(mensaje);
      console.log("Datos completos:", JSON.stringify(data, null, 2));
      
      // Mostrar alerta
      alert(mensaje);
    } catch (error) {
      console.error("Error en diagnóstico:", error);
      toast.error("Error al diagnosticar documentos: " + (error.response?.data?.detail || error.message));
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const batchRes = await api.get(`/batches/${batchId}`);
      const batchData = batchRes.data;
      setBatch(batchData);

      // Cargar datos relacionados en paralelo
      const promises = [];
      
      if (batchData.producto_id) {
        promises.push(
          api.get(`/products/${batchData.producto_id}`)
            .then(res => setProduct(res.data))
            .catch(() => {})
        );
      }
      if (batchData.proveedor_id) {
        promises.push(
          api.get(`/suppliers/${batchData.proveedor_id}`)
            .then(res => setSupplier(res.data))
            .catch(() => {})
        );
      }
      if (batchData.cliente_id) {
        promises.push(
          api.get(`/clients/${batchData.cliente_id}`)
            .then(res => setClient(res.data))
            .catch(() => {})
        );
      }
      
      // Cargar documentos del lote
      promises.push(
        api.get(`/documents?numero_lote=${batchData.numero_lote}`)
          .then(res => setBatchDocuments(res.data))
          .catch(() => setBatchDocuments([]))
      );
      
      await Promise.all(promises);
    } catch (error) {
      toast.error("Error al cargar el lote");
      navigate("/batches");
    } finally {
      setLoading(false);
    }
  };
  
  // Función para abrir/descargar un documento
  const handleOpenDocument = async (doc) => {
    try {
      const response = await api.get(`/documents/${doc.id}/download`, {
        responseType: "blob",
      });
      const blob = new Blob([response.data], { type: doc.tipo_archivo || "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch (error) {
      toast.error("Error al abrir el documento");
    }
  };
  
  // Obtener documentos visibles para el usuario según su rol
  const getVisibleDocuments = () => {
    if (isAdmin) {
      return batchDocuments;
    }
    if (isCliente) {
      return batchDocuments.filter(d => clientVisibleDocs.some(type => d.tipo_documento?.includes(type)));
    }
    // Proveedor: todos excepto factura_cliente
    return batchDocuments.filter(d => !d.tipo_documento?.includes('factura_cliente'));
  };
  
  // Obtener label legible del tipo de documento
  const getDocLabel = (tipoDoc) => {
    for (const [key, label] of Object.entries(docTypeLabels)) {
      if (tipoDoc?.includes(key)) return label;
    }
    return tipoDoc || "Documento";
  };

  // Formatear fecha SIN conversión de zona horaria
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const datePart = dateStr.split('T')[0];
    const parts = datePart.split('-');
    if (parts.length === 3) {
      const [year, month, day] = parts;
      return `${day}/${month}/${year}`;
    }
    return dateStr;
  };

  const formatNumber = (num, decimals = 2) => {
    if (num === null || num === undefined) return "-";
    return Number(num).toLocaleString("es-ES", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-sky-600" />
      </div>
    );
  }

  if (!batch) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Lote no encontrado</p>
        <Link to="/batches" className="text-sky-600 hover:underline">Volver a lotes</Link>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="batch-view-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate("/batches")}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver
          </Button>
          <div>
            <div className="flex items-center gap-2">
              {batch.documentacion_completa && (
                <CheckCircle2 className="w-6 h-6 text-green-600" title="Documentación completa para operador logístico" />
              )}
              <h1 className="text-2xl font-bold text-slate-800">Lote {batch.numero_lote}</h1>
            </div>
            <p className="text-sm text-slate-500">Control #{batch.numero_control}</p>
          </div>
          <Badge className={statusColors[batch.estado]}>
            {statusLabels[batch.estado]}
          </Badge>
        </div>
        <div className="flex flex-wrap gap-2">
          {/* Editar - Solo admin y proveedor */}
          {!isProveedor && user?.role !== "cliente" && (
            <Button variant="outline" onClick={() => navigate(`/batches/${batchId}`)}>
              <Edit className="w-4 h-4 mr-2" />
              Editar
            </Button>
          )}
          {isProveedor && (
            <Button variant="outline" onClick={() => navigate(`/batches/${batchId}`)}>
              <Edit className="w-4 h-4 mr-2" />
              Editar
            </Button>
          )}
          {/* Factura Origen - Solo admin y proveedor */}
          {user?.role !== "cliente" && (
            <Button variant="outline" onClick={() => navigate(`/batches/${batchId}/invoice-origin`)}>
              <Receipt className="w-4 h-4 mr-2" />
              Factura Origen
            </Button>
          )}
          {/* Factura Cliente: solo admin y cliente */}
          {(isAdmin || isCliente) && (
            <Button variant="outline" onClick={() => navigate(`/batches/${batchId}/invoice-client`)}>
              <Receipt className="w-4 h-4 mr-2" />
              Factura Cliente
            </Button>
          )}
          <Button variant="outline" onClick={() => navigate(`/batches/${batchId}/packing-list`)}>
            <ClipboardList className="w-4 h-4 mr-2" />
            Packing List
          </Button>
          <Button variant="outline" onClick={() => navigate(`/batches/${batchId}/labels`)}>
            <Tag className="w-4 h-4 mr-2" />
            Etiquetas
          </Button>
          {/* Informe CSO y Captura - Solo admin y proveedor */}
          {user?.role !== "cliente" && (
            <Button 
              variant="outline" 
              onClick={() => navigate(`/batches/${batchId}/informe-cso`)}
              className="bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100"
              data-testid="informe-cso-btn"
            >
              <ClipboardList className="w-4 h-4 mr-2" />
              Revisión CSO
            </Button>
          )}
          {/* Copia de Seguridad: solo admin */}
          {isAdmin && (
            <Button 
              variant="outline" 
              onClick={handleBackupLote}
              disabled={generatingBackup}
              className="bg-amber-50 border-amber-300 text-amber-700 hover:bg-amber-100"
              data-testid="backup-lote-btn"
            >
              {generatingBackup ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Archive className="w-4 h-4 mr-2" />
              )}
              Copia de seguridad LOTE
            </Button>
          )}
          {/* Regenerar todos los PDFs: solo admin */}
          {isAdmin && (
            <Button 
              variant="outline" 
              onClick={handleRegeneratePdfs}
              disabled={regeneratingPdfs}
              className="bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100"
              data-testid="regenerate-pdfs-btn"
            >
              {regeneratingPdfs ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              Regenerar PDFs
            </Button>
          )}
        </div>
      </div>

      {/* Documentos subidos - Acceso rápido */}
      {getVisibleDocuments().length > 0 && (
        <Card className="bg-emerald-50 border-emerald-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-emerald-800">
              <FileCheck className="w-5 h-5" />
              Documentos Disponibles
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {getVisibleDocuments().map((doc) => (
                <Button 
                  key={doc.id}
                  variant="outline" 
                  size="sm"
                  onClick={() => handleOpenDocument(doc)}
                  className="bg-white hover:bg-emerald-100 border-emerald-300 text-emerald-700"
                  data-testid={`doc-btn-${doc.tipo_documento}`}
                >
                  <Download className="w-4 h-4 mr-2" />
                  {getDocLabel(doc.tipo_documento)}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Estado de documentación obligatoria para operador logístico */}
      {!isCliente && (
        <Card className={batch.documentacion_completa ? "bg-green-50 border-green-200" : "bg-amber-50 border-amber-200"}>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className={`text-base flex items-center gap-2 ${batch.documentacion_completa ? "text-green-800" : "text-amber-800"}`}>
                {batch.documentacion_completa ? (
                  <CheckCircle2 className="w-5 h-5" />
                ) : (
                  <AlertCircle className="w-5 h-5" />
                )}
                {batch.documentacion_completa ? "Documentación Completa" : "Documentación Pendiente"}
                <span className="text-xs font-normal ml-2">(para operador logístico)</span>
              </CardTitle>
              {/* Botones de admin */}
              {isAdmin && (
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleDiagnosticar}
                    className="bg-white border-amber-300 text-amber-700 hover:bg-amber-100"
                    data-testid="diagnose-documents-btn"
                  >
                    <AlertCircle className="w-4 h-4 mr-2" />
                    Diagnosticar
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleOpenSendDocModal}
                    className="bg-white border-green-300 text-green-700 hover:bg-green-100"
                    data-testid="send-documentation-btn"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Enviar documentación
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {documentosObligatorios.map((doc) => {
                // Verificar si el documento está presente
                const tieneDoc = batchDocuments.some(d => 
                  d.tipo_documento === doc.key || 
                  (doc.key === "etiqueta" && d.tipo_documento?.startsWith("etiqueta"))
                );
                return (
                  <Badge 
                    key={doc.key}
                    variant={tieneDoc ? "default" : "outline"}
                    className={tieneDoc 
                      ? "bg-green-100 text-green-800 border-green-300" 
                      : "bg-white text-slate-500 border-slate-300"
                    }
                  >
                    {tieneDoc && <CheckCircle2 className="w-3 h-3 mr-1" />}
                    {doc.label}
                  </Badge>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Grid de información */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Información básica */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Información del Lote</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-500">Número de Lote:</span>
              <span className="font-semibold">{batch.numero_lote}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Nº Control:</span>
              <span className="font-semibold">{batch.numero_control}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Factura Origen:</span>
              <span className="font-mono">{batch.factura_origen || "-"}</span>
            </div>
            {/* Factura Destino: oculto para proveedores */}
            {!isProveedor && (
              <div className="flex justify-between">
                <span className="text-slate-500">Factura Destino:</span>
                <span className="font-mono">{batch.factura_destino || "-"}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-slate-500">Contenedor:</span>
              <span className="font-mono">{batch.contenedor || "-"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Precintos:</span>
              <span className="font-mono">{batch.precintos || "-"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">AWB:</span>
              <span className="font-mono">{batch.awb_aereo || "-"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">B/L:</span>
              <span className="font-mono">{batch.bl_maritimo || "-"}</span>
            </div>
            {/* DUA: visible para admin y proveedor */}
            {(isAdmin || isProveedor) && (
              <div className="flex justify-between">
                <span className="text-slate-500">DUA:</span>
                <span className="font-mono">{batch.dua || "-"}</span>
              </div>
            )}
            {/* Certificado Exportación: visible para admin y proveedor */}
            {(isAdmin || isProveedor) && (
              <div className="flex justify-between">
                <span className="text-slate-500">Cert. Exportación:</span>
                <span className="font-mono">{batch.certificado_exportacion || "-"}</span>
              </div>
            )}
            {/* Identificación Buque: solo admin */}
            {isAdmin && (
              <div className="flex justify-between">
                <span className="text-slate-500">Identif. Buque:</span>
                <span className="font-mono text-xs">{batch.identificacion_buque || "-"}</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Producto */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Producto</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-500">Nombre:</span>
              <span className="font-semibold">{product?.nombre || batch.producto_nombre || "-"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Especie:</span>
              <span>{product?.nombre_cientifico || batch.nombre_cientifico || "-"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Presentación:</span>
              <span>{batch.presentacion || product?.presentacion || "-"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Empaque:</span>
              <span>{batch.empaque || product?.empaque || "-"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Zona FAO:</span>
              <span>{batch.zona_fao || product?.zona_fao || "-"}</span>
            </div>
          </CardContent>
        </Card>

        {/* Pesos */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Pesos y Cantidades</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-500">Peso Neto:</span>
              <span className="font-semibold">{formatNumber(batch.peso_neto)} Kg</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Peso Bruto:</span>
              <span>{formatNumber(batch.peso_bruto)} Kg</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Tara:</span>
              <span>{formatNumber(batch.tara)} Kg</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Bultos:</span>
              <span>{batch.bultos || "-"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Palets:</span>
              <span>{batch.palets || "-"}</span>
            </div>
          </CardContent>
        </Card>

        {/* Proveedor - Oculto para proveedores (redundante) */}
        {!isProveedor && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Proveedor</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">Nombre:</span>
                <span className="font-semibold">{supplier?.nombre || batch.proveedor_nombre || "-"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">País Origen:</span>
                <span>{batch.pais_origen || "-"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Puerto Desembarco:</span>
                <span>{batch.puerto_desembarco || "-"}</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Cliente - NO visible para proveedores */}
        {!isProveedor && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Cliente</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">Nombre:</span>
                <span className="font-semibold">{client?.nombre || batch.cliente_nombre || "-"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">CIF:</span>
                <span className="font-mono">{client?.identificacion_fiscal || "-"}</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Fechas */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Fechas</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-500">Fecha Captura:</span>
              <span>{formatDate(batch.fecha_captura)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Fecha Congelación:</span>
              <span>{formatDate(batch.fecha_congelacion)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Fecha Caducidad:</span>
              <span>{formatDate(batch.fecha_caducidad)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Fecha Embarque:</span>
              <span>{formatDate(batch.fecha_embarque)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Salida Buque:</span>
              <span>{formatDate(batch.salida_buque)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Fecha Llegada Estimada:</span>
              <span>{formatDate(batch.llegada_estimada)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Fecha Llegada:</span>
              <span>{formatDate(batch.fecha_llegada)}</span>
            </div>
          </CardContent>
        </Card>

        {/* Datos CSO - Solo para admin */}
        {isAdmin && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Datos CSO / Logística</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">Lugar Destino:</span>
                <span className="font-semibold">{batch.lugar_destino_nombre || "-"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Puesto Control:</span>
                <span>{batch.puesto_control_nombre || "-"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Tipo Envío:</span>
                <span className="capitalize">{batch.tipo_envio || "-"}</span>
              </div>
              {batch.logistica_nombre && (
                <div className="flex justify-between">
                  <span className="text-slate-500">Op. Logístico:</span>
                  <span>{batch.logistica_nombre}</span>
                </div>
              )}
              {batch.transporte_nombre && (
                <div className="flex justify-between">
                  <span className="text-slate-500">Transporte:</span>
                  <span>{batch.transporte_nombre}</span>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Precios - NO visible para proveedores */}
        {!isProveedor && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Precios</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">Precio Coste:</span>
                <span className="font-semibold">
                  {formatNumber(batch.precio_coste)} $/Kg
                </span>
              </div>
              {batch.cotizacion_eur_usd > 0 && (
                <div className="flex justify-between">
                  <span className="text-slate-500">Precio Coste (EUR):</span>
                  <span className="font-semibold text-slate-600">
                    {formatNumber((batch.precio_coste || 0) / batch.cotizacion_eur_usd)} €/Kg
                  </span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-slate-500">Precio Venta:</span>
                <span className="font-semibold">{formatNumber(batch.precio_venta)} €/Kg</span>
              </div>
              {/* Mostrar descuento aplicado si existe */}
              {(batch.descuento_porcentaje || batch.descuento_euros) && (
                <div className="flex justify-between text-amber-700 bg-amber-50 -mx-2 px-2 py-1 rounded">
                  <span>Descuento:</span>
                  <span className="font-semibold">
                    {batch.descuento_porcentaje 
                      ? `${formatNumber(batch.descuento_porcentaje, 4)}%`
                      : `${formatNumber(batch.descuento_euros, 4)} €/Kg`
                    }
                  </span>
                </div>
              )}
              {/* Precio con descuento */}
              {(batch.descuento_porcentaje || batch.descuento_euros) && (
                <div className="flex justify-between">
                  <span className="text-slate-500">Precio con Descuento:</span>
                  <span className="font-semibold text-emerald-700">
                    {formatNumber(
                      batch.descuento_porcentaje 
                        ? (batch.precio_venta || 0) * (1 - (batch.descuento_porcentaje / 100))
                        : (batch.precio_venta || 0) - (batch.descuento_euros || 0)
                    )} €/Kg
                  </span>
                </div>
              )}
              <div className="flex justify-between border-t pt-2 mt-2">
                <span className="text-slate-500">Importe Total:</span>
                <span className="font-bold text-emerald-600">
                  {formatNumber(
                    (() => {
                      const precioBase = batch.precio_venta || 0;
                      let precioFinal = precioBase;
                      if (batch.descuento_porcentaje) {
                        precioFinal = precioBase * (1 - (batch.descuento_porcentaje / 100));
                      } else if (batch.descuento_euros) {
                        precioFinal = precioBase - batch.descuento_euros;
                      }
                      return precioFinal * (batch.peso_neto || 0);
                    })()
                  )} €
                </span>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Tabla de Tallas */}
      {batch.tallas && batch.tallas.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Desglose de Tallas</CardTitle>
          </CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2">Talla</th>
                  <th className="text-right py-2">Bultos</th>
                  <th className="text-right py-2">Peso/Bulto (Kg)</th>
                  <th className="text-right py-2">Total (Kg)</th>
                </tr>
              </thead>
              <tbody>
                {batch.tallas.map((t, idx) => (
                  <tr key={idx} className="border-b">
                    <td className="py-2 font-medium">{t.talla}</td>
                    <td className="text-right py-2">{t.num_bultos}</td>
                    <td className="text-right py-2">{formatNumber(t.peso_por_bulto)}</td>
                    <td className="text-right py-2 font-semibold">{formatNumber((t.num_bultos || 0) * (t.peso_por_bulto || 0))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Observaciones */}
      {batch.observaciones && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Observaciones</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-600 whitespace-pre-wrap">{batch.observaciones}</p>
          </CardContent>
        </Card>
      )}

      {/* Modal para enviar documentación */}
      <Dialog open={showSendDocModal} onOpenChange={setShowSendDocModal}>
        <DialogContent className="sm:max-w-[600px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-green-600" />
              Enviar Documentación
            </DialogTitle>
            <DialogDescription>
              Envía la documentación del lote {batch?.numero_lote} por email
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Info del lote */}
            <div className="bg-slate-50 p-3 rounded-lg text-sm">
              <p><strong>Contenedor:</strong> {docInfo?.contenedor || batch?.contenedor || "-"}</p>
              <p><strong>Lote:</strong> {docInfo?.numero_lote || batch?.numero_lote || "-"}</p>
            </div>

            {/* Operador Logístico */}
            <div className="space-y-2 border rounded-lg p-3">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="enviar-operador"
                  checked={enviarAOperador}
                  onCheckedChange={setEnviarAOperador}
                />
                <Label htmlFor="enviar-operador" className="font-medium">
                  Enviar al Operador Logístico
                </Label>
              </div>
              {enviarAOperador && (
                <div className="ml-6 space-y-3">
                  <div>
                    <Label className="text-xs text-slate-500">Email del operador</Label>
                    <Input 
                      type="email"
                      value={emailOperador}
                      onChange={(e) => setEmailOperador(e.target.value)}
                      placeholder="operador@ejemplo.com"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Nombre destinatario (para el saludo del email)</Label>
                    <Input 
                      type="text"
                      value={nombreOperador}
                      onChange={(e) => setNombreOperador(e.target.value)}
                      placeholder="Ej: Juan, María..."
                      className="mt-1"
                    />
                    {docInfo?.nombre_operador && !nombreOperador && (
                      <p className="text-xs text-green-600 mt-1">
                        Detectado: {docInfo.nombre_operador}
                      </p>
                    )}
                  </div>
                  {/* Selección de documentos */}
                  <div>
                    <Label className="text-xs text-slate-500 mb-2 block">Documentos a enviar:</Label>
                    <div className="grid grid-cols-2 gap-1">
                      {TODOS_DOCS_ENVIO.map(doc => (
                        <label key={doc.key} className="flex items-center space-x-2 text-xs cursor-pointer hover:bg-slate-50 p-1 rounded">
                          <input
                            type="checkbox"
                            checked={docsSeleccionadosOperador.includes(doc.key)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setDocsSeleccionadosOperador([...docsSeleccionadosOperador, doc.key]);
                              } else {
                                setDocsSeleccionadosOperador(docsSeleccionadosOperador.filter(k => k !== doc.key));
                              }
                            }}
                            className="rounded border-gray-300"
                          />
                          <span>{doc.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Cliente */}
            <div className="space-y-2 border rounded-lg p-3">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="enviar-cliente"
                  checked={enviarACliente}
                  onCheckedChange={setEnviarACliente}
                />
                <Label htmlFor="enviar-cliente" className="font-medium">
                  Enviar al Cliente
                </Label>
              </div>
              {enviarACliente && (
                <div className="ml-6 space-y-3">
                  <div>
                    <Label className="text-xs text-slate-500">Email del cliente</Label>
                    <Input 
                      type="email"
                      value={emailCliente}
                      onChange={(e) => setEmailCliente(e.target.value)}
                      placeholder="cliente@ejemplo.com"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Nombre destinatario (para el saludo del email)</Label>
                    <Input 
                      type="text"
                      value={nombreCliente}
                      onChange={(e) => setNombreCliente(e.target.value)}
                      placeholder="Ej: Juan, María..."
                      className="mt-1"
                    />
                    {docInfo?.nombre_cliente && !nombreCliente && (
                      <p className="text-xs text-green-600 mt-1">
                        Detectado: {docInfo.nombre_cliente}
                      </p>
                    )}
                  </div>
                  {/* Selección de documentos */}
                  <div>
                    <Label className="text-xs text-slate-500 mb-2 block">Documentos a enviar:</Label>
                    <div className="grid grid-cols-2 gap-1">
                      {TODOS_DOCS_ENVIO.filter(d => d.key !== "factura_origen").map(doc => (
                        <label key={doc.key} className="flex items-center space-x-2 text-xs cursor-pointer hover:bg-slate-50 p-1 rounded">
                          <input
                            type="checkbox"
                            checked={docsSeleccionadosCliente.includes(doc.key)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setDocsSeleccionadosCliente([...docsSeleccionadosCliente, doc.key]);
                              } else {
                                setDocsSeleccionadosCliente(docsSeleccionadosCliente.filter(k => k !== doc.key));
                              }
                            }}
                            className="rounded border-gray-300"
                          />
                          <span>{doc.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Proveedor */}
            <div className="space-y-2 border rounded-lg p-3">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="enviar-proveedor"
                  checked={enviarAProveedor}
                  onCheckedChange={setEnviarAProveedor}
                />
                <Label htmlFor="enviar-proveedor" className="font-medium">
                  Enviar al Proveedor
                </Label>
              </div>
              {enviarAProveedor && (
                <div className="ml-6 space-y-3">
                  <div>
                    <Label className="text-xs text-slate-500">Email del proveedor</Label>
                    <Input 
                      type="email"
                      value={emailProveedor}
                      onChange={(e) => setEmailProveedor(e.target.value)}
                      placeholder="proveedor@ejemplo.com"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Nombre destinatario (para el saludo del email)</Label>
                    <Input 
                      type="text"
                      value={nombreProveedor}
                      onChange={(e) => setNombreProveedor(e.target.value)}
                      placeholder="Ej: Juan, María..."
                      className="mt-1"
                    />
                    {docInfo?.nombre_proveedor && !nombreProveedor && (
                      <p className="text-xs text-green-600 mt-1">
                        Detectado: {docInfo.nombre_proveedor}
                      </p>
                    )}
                  </div>
                  {/* Selección de documentos */}
                  <div>
                    <Label className="text-xs text-slate-500 mb-2 block">Documentos a enviar:</Label>
                    <div className="grid grid-cols-2 gap-1">
                      {TODOS_DOCS_ENVIO.map(doc => (
                        <label key={doc.key} className="flex items-center space-x-2 text-xs cursor-pointer hover:bg-slate-50 p-1 rounded">
                          <input
                            type="checkbox"
                            checked={docsSeleccionadosProveedor.includes(doc.key)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setDocsSeleccionadosProveedor([...docsSeleccionadosProveedor, doc.key]);
                              } else {
                                setDocsSeleccionadosProveedor(docsSeleccionadosProveedor.filter(k => k !== doc.key));
                              }
                            }}
                            className="rounded border-gray-300"
                          />
                          <span>{doc.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Campo de Comentarios */}
            <div className="space-y-2 border-t pt-4">
              <Label className="text-sm font-medium">Comentarios (opcional)</Label>
              <textarea
                value={comentariosEmail}
                onChange={(e) => setComentariosEmail(e.target.value)}
                placeholder="Escribe aquí cualquier nota adicional que quieras incluir en el email..."
                className="w-full min-h-[80px] p-2 border rounded-md text-sm resize-y focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-slate-400">
                Este texto se añadirá al final del email si lo rellenas.
              </p>
            </div>

            {/* Avisos */}
            {(!emailOperador && enviarAOperador) && (
              <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-700">
                ⚠️ No se encontró email del operador logístico. Introdúcelo manualmente o desmarca esta opción.
              </div>
            )}
            {(!emailCliente && enviarACliente) && (
              <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-700">
                ⚠️ No se encontró email del cliente. Introdúcelo manualmente o desmarca esta opción.
              </div>
            )}
            {(!emailProveedor && enviarAProveedor) && (
              <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-700">
                ⚠️ No se encontró email del proveedor. Introdúcelo manualmente o desmarca esta opción.
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSendDocModal(false)} disabled={sendingDocs}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSendDocumentation} 
              disabled={sendingDocs || (!enviarAOperador && !enviarACliente && !enviarAProveedor)}
              className="bg-green-600 hover:bg-green-700"
            >
              {sendingDocs ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Enviar
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
